/**
 * BookingVoucher — microinteracción de confirmación post-booking.
 *
 * Una impresora térmica compacta "imprime" el comprobante de la reserva: el
 * papel se revela de arriba hacia abajo animando el ALTO de un contenedor con
 * overflow:hidden (`.voucher-paper-clip`, 0 → auto) — el contenido no se deforma
 * ni hace reflow del resto. Después aparece un check sutil y, como beat separado,
 * Konex "estampa" una nota "Yo te recuerdo" debajo del papel (descartable).
 *
 * Nota: se animaba `clip-path: inset()` pero framer no interpola bien ese valor
 * (saltaba directo al estado final) — de ahí el cambio a alto animado.
 *
 * - El voucher es DOM real (dl/dt/dd) — su contenido se puebla por props desde
 *   BookingSuccess, no es una imagen.
 * - `prefers-reduced-motion` y la 2da reserva de la sesión saltan la animación:
 *   el papel aparece con un fade corto y Konex sin transform.
 * - "Ya se vio" (`SEEN_KEY`) se marca cuando la animación TERMINA, no al montar:
 *   así el doble montaje de `React.StrictMode` en dev no se auto-envenena (el
 *   primer montaje descartado marcaba la sesión y el real saltaba la impresión),
 *   y en prod "visto" significa que el usuario efectivamente la vio.
 * - La nota de Konex vive en su propio slot DEBAJO del papel (nunca encima del
 *   pie ni del zigzag). El slot reserva su alto → no hay reflow cuando aparece.
 *   No se anima al montar: espera a entrar en viewport (`useInView`, once) y
 *   recién ahí "se pega".
 * - Anuncio `role="status"` inmediato, independiente del timeline visual.
 *
 * Solo estética de la pantalla de éxito — no toca `create_consultation` ni nada
 * del módulo crítico de creación de consultas.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import './BookingVoucher.css';

const KONEX_AVATAR = '/assets/img/konex/konex-calido.webp';
const SEEN_KEY = 'cc:voucher:seen';

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

export interface BookingVoucherRow {
  label: string;
  value: string;
  /** Fuerza mono en el valor (código de reserva). */
  mono?: boolean;
}

interface BookingVoucherProps {
  /** Filas ya formateadas, en orden. */
  rows: BookingVoucherRow[];
  /** "Emitido el 3 sep 2026, 14:32" — pie del papel. */
  emittedLabel: string;
  /** Texto para lectores de pantalla, se anuncia al montar. */
  announcement: string;
}

const readSeen = (): boolean => {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const markSeen = (): void => {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* private mode / storage bloqueado — no pasa nada */
  }
};

type Phase = 'printing' | 'confirmed' | 'konex';

const BookingVoucher: React.FC<BookingVoucherProps> = ({ rows, emittedLabel, announcement }) => {
  const prefersReduced = useReducedMotion();
  // La 2da vez en la misma sesión ("Agendar otra hora") no repite los 2 s de impresora.
  const [alreadySeen] = useState(readSeen);
  const fast = Boolean(prefersReduced) || alreadySeen;

  const [phase, setPhase] = useState<Phase>(fast ? 'konex' : 'printing');
  const [ledOn, setLedOn] = useState(false);
  const [konexDismissed, setKonexDismissed] = useState(false);

  // La nota de Konex sólo "se pega" cuando su zona entra en viewport. Si la
  // impresión terminó antes de que el usuario scrollee, la animación lo espera
  // en vez de dispararse fuera de pantalla.
  const konexAnchorRef = useRef<HTMLSpanElement>(null);
  const konexInView = useInView(konexAnchorRef, { once: true, margin: '0px 0px -24px 0px' });

  useEffect(() => {
    if (fast) {
      // Camino rápido: no hay impresión que ver, la sesión ya la conoce.
      markSeen();
      return;
    }
    const activate = window.setTimeout(() => setLedOn(true), 150);
    return () => window.clearTimeout(activate);
  }, [fast]);

  // El papel terminó de "salir" → check sutil, y 320 ms después la nota de Konex.
  const handlePaperSettled = () => {
    if (fast || phase !== 'printing') return;
    setPhase('confirmed');
    setLedOn(false);
    window.setTimeout(() => {
      setPhase('konex');
      markSeen(); // recién acá: la impresión ya se vio entera
    }, 320);
  };

  // El "papel" se revela de arriba hacia abajo animando el ALTO de un contenedor
  // con overflow:hidden (el contenido no se deforma, a diferencia de scaleY, y es
  // más confiable que animar clip-path en framer). Sale de abajo del labio de la
  // impresora, como si se estuviera imprimiendo.
  const clipMotion = fast
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, height: 'auto' as const },
        transition: { duration: 0.24, ease: EASE_OUT },
      }
    : {
        initial: { height: 0 },
        animate: { height: 'auto' as const },
        // Sin `delay`: framer, durante un delay, muestra el alto natural (flash
        // del papel entero antes de "imprimirse"). Arranca ya.
        transition: { duration: 1.4, ease: EASE_OUT },
      };

  const showCheck = phase !== 'printing';
  const showKonex = phase === 'konex' && konexInView && !konexDismissed;

  // Entrada "post-it": baja desde arriba y se asienta con un settle suave de
  // resorte, con una inclinación mínima (2° → -1.5°) — apenas un gesto de "nota
  // pegada a mano", no un cuadro torcido.
  // `prefers-reduced-motion` cae a un fade plano, sin travel ni rotación.
  const konexMotion = prefersReduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.18 },
      }
    : {
        initial: { opacity: 0, y: -14, rotate: 2, scale: 0.99 },
        animate: { opacity: 1, y: 0, rotate: -1.5, scale: 1 },
        transition: { type: 'spring' as const, stiffness: 180, damping: 18, mass: 0.9 },
      };

  const emitted = useMemo(() => emittedLabel, [emittedLabel]);

  return (
    <div className="voucher-stage" role="group" aria-label="Comprobante de tu reserva">
      <p className="voucher-sr-only" role="status">
        {announcement}
      </p>

      <div className="voucher-machine" aria-hidden="true">
        <div className="voucher-printer">
          <span className="voucher-printer__vent" />
          <span className={`voucher-printer__led${ledOn ? ' is-on' : ''}`} />
          <span className="voucher-printer__lip" />
        </div>
      </div>

      <div className="voucher-paper-wrap">
        <motion.div
          className="voucher-paper-clip"
          initial={clipMotion.initial}
          animate={clipMotion.animate}
          transition={clipMotion.transition}
          onAnimationComplete={handlePaperSettled}
        >
          <article className="voucher-paper">
            <div className="voucher-paper__sheen" aria-hidden="true" />

            {showCheck && (
              <motion.div
                className="voucher-paper__check"
                initial={fast ? false : { scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={fast ? { duration: 0 } : { duration: 0.34, ease: EASE_OUT }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                  <path
                    d="M20 6 9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
            )}

            <header className="voucher-paper__brand">
              <strong>CONECTEMOS CHILE</strong>
              <span>COMPROBANTE DE RESERVA</span>
            </header>

            <div className="voucher-paper__barcode" aria-hidden="true" />

            <dl className="voucher-paper__rows">
              {rows.map((row) => (
                <div className="voucher-paper__row" key={row.label}>
                  <dt>{row.label}</dt>
                  <dd className={row.mono ? 'is-mono' : undefined}>{row.value}</dd>
                </div>
              ))}
            </dl>

            <p className="voucher-paper__foot">{emitted}</p>
          </article>

          {/* Borde dentado: último en revelarse, "cierra" la impresión. */}
          <div className="voucher-paper__tear" aria-hidden="true" />
        </motion.div>
      </div>

      {/* Nota de Konex — slot propio DEBAJO del papel. El slot reserva el alto
          para que la nota (absoluta) no empuje el contenido funcional al aparecer. */}
      <div className="voucher-konex-slot">
        {/* Sentinela: dispara el "pegado" al entrar en viewport. */}
        <span ref={konexAnchorRef} className="voucher-konex-anchor" aria-hidden="true" />

        <AnimatePresence>
          {showKonex && (
            <motion.aside
              className="voucher-konex"
              role="note"
              aria-label="Recordatorio de Konex"
              initial={konexMotion.initial}
              animate={konexMotion.animate}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
              transition={konexMotion.transition}
            >
              <button
                type="button"
                className="voucher-konex__close"
                aria-label="Ocultar recordatorio de Konex"
                onClick={() => setKonexDismissed(true)}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
                  <path
                    d="M6 6l12 12M18 6 6 18"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <img className="voucher-konex__avatar" src={KONEX_AVATAR} alt="" width={44} height={44} />
              <div className="voucher-konex__body">
                <p className="voucher-konex__title">Yo te recuerdo 👋</p>
                <p className="voucher-konex__text">
                  Te aviso por correo y por WhatsApp antes de tu sesión. Vos no tenés que hacer nada.
                </p>
                <p className="voucher-konex__sign">— Konex</p>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingVoucher;
